import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { msg, t } from '@lingui/macro';
import { Trans, useLingui } from '@lingui/react';
import { identity } from 'lodash';
import { Divider, Icon } from 'semantic-ui-react';

import {
  addExtraLanguage,
  deleteExtraLanguage,
  updateTranslationElement,
} from 'actions/survey';
import {
  campaignLanguageSelectorFactory,
  faqItemsSelectorFactory,
} from 'selectors/campaign';
import { campaignDraftLoadingSelector } from 'selectors/campaignLoading';
import {
  MULTICHOICE_QUESTION_ITEM,
  QUESTION_TYPES_MAP,
  QuestionLabelWithIcon,
  displayHomeScreenSelector,
  endScreensSelectorFactory,
  extraLanguagesSelectorFactory,
  homeScreenSelectorFactory,
  surveyQuestionsSelector,
} from 'selectors/survey';

import * as Sentry from '@sentry/react';
import { transparentize } from 'polished';

import EmptyDataPage from 'components/ui/EmptyDataPage';
import NavigationSectionHeader from 'components/ui/SectionHeader';
import StyledSegment from 'components/ui/Segment';
import LabelWithIcon from 'components/ui/SurveyQuestion';
import { TabButton } from 'components/ui/button/TabButton';
import FlagItem from 'components/ui/cells/FlagItem';
import { StyledDropdown } from 'components/ui/inputs/Dropdown';
import { Column, Container, LeftColumn } from 'components/ui/layout/Page';
import ManagementList from 'components/ui/management-list/ManagementList';
import emptyPageUrl from 'components/ui/svg/undraw_preferences_2bda.svg';

import capitalizedTranslation from 'utils/i18n';
import { getCampaignLanguages } from 'utils/types';

import * as svars from 'assets/style/variables';

import {
  TranslationBlock,
  languageElementFactory,
} from './translationElements';

const QUESTION_TRANSLATIONS_FIELDS = [
  {
    fieldName: 'section_name',
    translationsFieldName: 'section_name_translations',
    label: msg({ id: 'question-title' }),
    singleLine: true,
    basic: true,
  },
  {
    fieldName: 'title',
    translationsFieldName: 'title_translations',
    label: msg({ id: 'title' }),
  },
  {
    fieldName: 'description',
    translationsFieldName: 'description_translations',
    label: msg({ id: 'description' }),
  },
];
const MULTICHOICE_QUESTION_TRANSLATIONS_FIELDS = [
  ...QUESTION_TRANSLATIONS_FIELDS,
  {
    multiKey: 'options',
    fieldName: 'display_name',
    translationsFieldName: 'display_name_translations',
    label: msg({ id: 'options' }),
  },
];

const SCREEN_TRANSLATION_FIELDS = [
  {
    fieldName: 'tagline',
    translationsFieldName: 'tagline_translations',
    label: msg({ id: 'tagline' }),
    singleLine: true,
    basic: true,
  },
  {
    fieldName: 'title',
    translationsFieldName: 'title_translations',
    label: msg({ id: 'title' }),
    singleLine: true,
    basic: true,
  },
  {
    fieldName: 'description',
    translationsFieldName: 'description_translations',
    label: msg({ id: 'description' }),
  },
  {
    fieldName: 'button_label',
    translationsFieldName: 'button_label_translations',
    label: msg({ id: 'button-label' }),
    singleLine: true,
    basic: true,
  },
];

const FAQ_ITEM_TRANSLATIONS_FIELDS = [
  {
    fieldName: 'header',
    translationsFieldName: 'header_translations',
    label: msg({ id: 'header' }),
  },
  {
    fieldName: 'content',
    translationsFieldName: 'content_translations',
    label: msg({ id: 'content' }),
  },
];

const DEFAULT_SORTED = { key: 'id', desc: false };

function CustomizeTranslationTab() {
  useLingui();
  const { campaignId } = useParams();
  const dispatch = useDispatch();
  const campaignLanguage = useSelector(
    campaignLanguageSelectorFactory(campaignId)
  );
  const extraLanguages = useSelector(
    extraLanguagesSelectorFactory(campaignId, true)
  );
  const questions = useSelector(surveyQuestionsSelector(campaignId, true));
  const faqItems = useSelector(faqItemsSelectorFactory(campaignId));
  const displayHomeScreen = useSelector(
    displayHomeScreenSelector(campaignId, true)
  );
  const homeScreen = useSelector(homeScreenSelectorFactory(campaignId, true));

  const endScreens = useSelector(endScreensSelectorFactory(campaignId, true));
  const [selectedExtraLanguage, setSelectedExtraLanguage] = useState(
    extraLanguages.length > 0 ? extraLanguages[0] : null
  );
  const availableLanguages = useMemo(
    () =>
      getCampaignLanguages(true).filter(
        ({ value }) =>
          value !== campaignLanguage && !extraLanguages?.includes(value)
      ),
    [campaignLanguage, extraLanguages]
  );
  const campaignConfigurationIsLoading = useSelector(
    campaignDraftLoadingSelector
  );

  const onAddExtraLanguage = useCallback(
    (e, { value }) => {
      dispatch(addExtraLanguage(campaignId, value))
        .then(() => {
          setSelectedExtraLanguage(value);
        })
        .catch((error) => {
          Sentry.captureException(error);
          // Optionally show an error message to the user
        });
    },
    [dispatch, campaignId]
  );
  const languageElement = useMemo(
    () =>
      languageElementFactory(
        (language) => () => {
          dispatch(deleteExtraLanguage(campaignId, language));
        },
        campaignConfigurationIsLoading
      ),
    [dispatch, campaignId, campaignConfigurationIsLoading]
  );
  const onTranslate = useCallback(
    (campaignFieldName, elementIdOrIndex) =>
      (translationsFieldName, nestedItemName, nestedFieldName, nestedId) =>
      (value) =>
        dispatch(
          updateTranslationElement(
            campaignId,
            elementIdOrIndex,
            selectedExtraLanguage,
            campaignFieldName,
            translationsFieldName,
            nestedItemName,
            nestedFieldName,
            nestedId,
            value
          )
        ),
    [dispatch, campaignId, selectedExtraLanguage]
  );
  if (!extraLanguages?.length) {
    if (campaignConfigurationIsLoading) {
      return null;
    }
    return (
      <EmptyDataPage
        layout="row"
        headerText={t({ id: 'empty-survey-translation-title' })}
        illustrationUrl={emptyPageUrl}
        actionComponent={
          <>
            <Trans id="default-language" render={capitalizedTranslation} /> :
            <FlagItem language={campaignLanguage} />
            <Divider />
            <StyledDropdown
              trigger={
                <LabelWithIcon
                  icon="add"
                  label={t({ id: 'add-extra-language' })}
                  iconSize="small"
                  style={{ marginLeft: svars.spaceNormal }}
                />
              }
              disabled={
                campaignConfigurationIsLoading || !availableLanguages?.length
              }
              pointing="top right"
              floating
              basic
              icon={null}
              options={availableLanguages}
              onChange={onAddExtraLanguage}
            />
            {/* <p style={{ marginTop: svars.spaceNormalLarge }}>
              <Trans id="need-help-check-out-these" />
            </p>
            <ul style={{ lineHeight: '1.6' }}>
              {helpLinks.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <HelpcrunchButtonLink
                    ButtonComponent={AnalyticsAwareLink}
                    base
                    articleId={link.articleId}
                    content={
                      <Trans
                        id={link.content}
                        render={capitalizedTranslation}
                      />
                    }
                  />
                </li>
              ))}
            </ul> */}
          </>
        }
        maxHeight="300px"
      />
    );
  }

  const isDisabled =
    campaignConfigurationIsLoading || !availableLanguages?.length;
  return (
    <Container>
      <LeftColumn width={2} style={{ minWidth: '200px' }}>
        <NavigationSectionHeader
          title={t({ id: 'extra-languages' })}
          action={
            <StyledDropdown
              trigger={
                <span
                  style={{
                    float: 'right',
                    overflowX: 'hidden',
                  }}
                >
                  <TabButton fitted="true" disabled={isDisabled}>
                    <Icon
                      data-testid="bo-campaign-add-survey-extra-language"
                      name="add"
                      style={{ margin: 0 }}
                    />
                  </TabButton>
                </span>
              }
              style={{ minWidth: 0 }}
              disabled={isDisabled}
              pointing="top right"
              floating
              basic
              icon={null}
              options={availableLanguages}
              onChange={onAddExtraLanguage}
            />
          }
        />

        <ManagementList
          compact
          rowIdAccessor={identity}
          items={extraLanguages}
          onRowClick={setSelectedExtraLanguage}
          renderItemRow={languageElement}
          withTextFilter={false}
          isRowActive={(row) => row === selectedExtraLanguage}
          defaultSorted={DEFAULT_SORTED}
          actions={null}
          style={{ minHeight: '6rem' }}
        />
        <Divider fitted />
      </LeftColumn>
      <Column width={14}>
        <StyledSegment
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: 0,
          }}
        >
          <Container style={{ flexShrink: 0 }}>
            <Column
              width={7}
              layout="row"
              style={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <Trans id="default-language" render={capitalizedTranslation} /> :
              <FlagItem
                language={campaignLanguage}
                style={{ marginLeft: svars.spaceMediumLarge }}
              />
            </Column>
            <Column width={2}>
              <Icon
                name="arrows alternate horizontal"
                size="large"
                style={{
                  margin: '0 auto',
                  display: 'block',
                  color: svars.fontColorLighter,
                }}
              />
            </Column>
            <Column
              width={7}
              layout="row"
              style={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <Trans id="target-language" render={capitalizedTranslation} /> :
              <FlagItem
                language={selectedExtraLanguage}
                style={{ marginLeft: svars.spaceMediumLarge }}
              />
            </Column>
          </Container>
          <Divider fitted />
          <div style={{ overflow: 'clip auto' }}>
            {displayHomeScreen ? (
              <TranslationBlock
                key={homeScreen.id}
                element={homeScreen}
                fields={SCREEN_TRANSLATION_FIELDS}
                header={t({ id: 'home-screen' })}
                onTranslate={onTranslate('home_screen', homeScreen.id)}
                selectedExtraLanguage={selectedExtraLanguage}
                originalLanguage={campaignLanguage}
              />
            ) : null}

            {questions.map((question, i) => (
              <TranslationBlock
                key={question.id}
                element={question}
                fields={
                  question.type === MULTICHOICE_QUESTION_ITEM.value
                    ? MULTICHOICE_QUESTION_TRANSLATIONS_FIELDS
                    : QUESTION_TRANSLATIONS_FIELDS
                }
                header={
                  <QuestionLabelWithIcon
                    question={question}
                    questionNumber={i + 1}
                    withIcon
                    withLabel={false}
                  />
                }
                onTranslate={onTranslate('questions', question.id)}
                selectedExtraLanguage={selectedExtraLanguage}
                headerBackground={transparentize(
                  0.95,
                  QUESTION_TYPES_MAP[question.type]?.iconColor ||
                    svars.colorLightestGrey
                )}
                originalLanguage={campaignLanguage}
              />
            ))}
            {endScreens.map((endScreen, i) => (
              <TranslationBlock
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                element={endScreen}
                fields={SCREEN_TRANSLATION_FIELDS}
                header={`${t({ id: 'end-screen' })} ${i + 1}`}
                onTranslate={onTranslate('end_screens', i)}
                selectedExtraLanguage={selectedExtraLanguage}
                headerBackground={transparentize(
                  0.95,
                  svars.TEXT_BACKGROUNDS[i]
                )}
                originalLanguage={campaignLanguage}
              />
            ))}
            {faqItems.map((faqItem, i) => (
              <TranslationBlock
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                element={faqItem}
                fields={FAQ_ITEM_TRANSLATIONS_FIELDS}
                header={`${t({ id: 'faq-item' })} ${i + 1}`}
                onTranslate={onTranslate('faq_items', faqItem.id)}
                selectedExtraLanguage={selectedExtraLanguage}
                headerBackground={transparentize(
                  0.8,
                  svars.RATING_COLORS_YELLOW[i]
                )}
                originalLanguage={campaignLanguage}
              />
            ))}
          </div>
        </StyledSegment>
      </Column>
    </Container>
  );
}

export default CustomizeTranslationTab;
